import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ASSETS_DIMENSIONS } from '@app/core/constants/constants';
import { SetCurrentApp } from '@app/core/states/app.actions';
import { LogEvent, SetEventProperties } from '@app/core/states/event-tracking.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { UiModule } from '@app/ui';
import {
  CropperState,
  ImageCropperComponent,
} from '@app/ui/components/image-cropper/image-cropper.component';
import { UIState } from '@app/ui/states/ui.state';
import { TranslateModule } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { AppNameSectionComponent } from '../components/app-name-section/app-name-section.component';
import { AppSettingsNavComponent } from '../components/app-settings-nav/app-settings-nav.component';
import { LogoAssetsSectionComponent } from '../components/logo-assets-section/logo-assets-section.component';
import { MonetizationSectionComponent } from '../components/monetization-section/monetization-section.component';
import { SettingsConfirmationModalComponent } from '../components/settings-confirmation-modal/settings-confirmation-modal.component';
import { SplashscreenAssetsSectionComponent } from '../components/splashscreen-assets-section/splashscreen-assets-section.component';
import { AppSettingsNavService } from '../services/app-settings-nav.service';
import { AssetUiService } from '../services/assets-ui-service';
import {
  EmptyAssetState,
  FetchAssetsRequest,
  PostImageAssetsRequest,
  SaveCropperState,
  SetRawImage,
  StoreAppName,
  StoreAsset,
  UpdateAssetsRequest,
} from '../states/assets.actions';
import { AssetKey, AssetsState, constructAssetUrl } from '../states/assets.state';
import { PrivacySectionComponent } from '../components/privacy-section/privacy-section.component';

interface SettingsConfirmationResult {
  save: boolean;
  redirect: boolean;
}

@Component({
  selector: 'rk-app-settings-update',
  standalone: true,
  templateUrl: './app-settings-update.component.html',
  styleUrl: './app-settings-update.component.scss',
  imports: [
    MonetizationSectionComponent,
    PrivacySectionComponent,
    UiModule,
    AppNameSectionComponent,
    TranslateModule,
    LogoAssetsSectionComponent,
    SplashscreenAssetsSectionComponent,
    AsyncPipe,
    AppSettingsNavComponent,
  ],
})
export class AppSettingsUpdateComponent implements OnInit, OnDestroy {
  appName = new FormControl(null);
  flexDirection$: Observable<string>;
  rightColumnFlexProportion: number;
  assetWidth = 200;
  private readonly subscriptions: Subscription = new Subscription();

  @Select(AppConfigurationState.currentAppConfigName)
  currentAppConfigName$: Observable<string>;
  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;
  @Select(AppConfigurationState.availableOnAndroidTV)
  availableOnAndroidTV$: Observable<boolean>;
  @Select(UIState.menuWidth)
  menuWidth$: Observable<number>;
  @Select(AssetsState.isLoading)
  isLoading$: Observable<boolean>;
  @Select(AppConfigurationState.apiErrorMessage)
  apiErrorMessage$: Observable<string>;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly store: Store,
    private readonly assetUiService: AssetUiService,
    private readonly navService: AppSettingsNavService,
    private readonly matDialog: MatDialog,
    private readonly router: Router,
  ) {
    this.flexDirection$ = this.assetUiService.setupMainContainerFlexDirection();
  }

  ngOnInit() {
    // get route parameters
    this.subscriptions.add(
      this.route?.params.pipe(filter(params => !!params)).subscribe(params => {
        const appId = params['appId'];
        if (appId) {
          this.emtyAssets();
          this.fetchAssets(Number(appId));
          this.setCurrentAppId(Number(appId));
        }
      }),
    );

    // set the app name
    this.subscriptions.add(
      this.currentAppConfigName$
        .pipe(filter(config => !!config))
        .subscribe(name => this.appName.setValue(name)),
    );

    // react to app name changes
    this.subscriptions.add(
      this.appName.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(value => {
          this.store.dispatch(new StoreAppName(value));
        }),
    );
    // adjust UI
    this.subscriptions.add(
      this.assetUiService
        .setupAssetWidthCalculation(
          this.availableOnAndroidTV$,
          this.isCreationProcess$,
          this.menuWidth$,
        )
        .subscribe(width => {
          this.assetWidth = width;
        }),
    );

    this.subscriptions.add(
      combineLatest([this.availableOnAndroidTV$, this.isCreationProcess$]).subscribe(
        ([isAvailable, isCreationProcess]) => {
          this.rightColumnFlexProportion =
            this.assetUiService.getRightColumnFlexProportion(
              isAvailable,
              isCreationProcess,
            );
        },
      ),
    );

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: 'PageUpdateSettings',
        component: 'Page',
      }),
    );
    this.store.dispatch(new LogEvent('Page Viewed'));

    // setup scroll spy for nav
    this.setupScrollSpy();
  }

  private setupScrollSpy() {
    setTimeout(() => {
      const container = document.querySelector(
        '.settings-subcontainer .flex-1.overflow-y-auto',
      );

      if (!container) {
        return;
      }

      const sections = ['name', 'logos', 'monetization', 'privacy'];

      const onScroll = () => {
        // skip if scrolling from nav click
        if (this.navService.isScrollLocked()) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const scrollTop = container.scrollTop;
        const isAtBottom =
          container.scrollHeight - scrollTop - container.clientHeight < 10;

        // if at bottom, select last existing section
        if (isAtBottom) {
          for (const s of [...sections].reverse()) {
            if (document.getElementById(`section-${s}`)) {
              this.navService.setActiveSection(s as any);

              return;
            }
          }
        }

        // find section closest to top of container
        let active = sections[0];
        for (const s of sections) {
          const el = document.getElementById(`section-${s}`);
          if (el) {
            const elRect = el.getBoundingClientRect();
            const elTop = elRect.top - containerRect.top;
            if (elTop <= 100) {
              active = s;
            }
          }
        }
        this.navService.setActiveSection(active as any);
      };

      container.addEventListener('scroll', onScroll);
      this.subscriptions.add({
        unsubscribe: () => container.removeEventListener('scroll', onScroll),
      });
    }, 100);
  }

  async openCropper(key: AssetKey) {
    const rawImages = this.store.selectSnapshot(AssetsState.rawImages);
    const assets = this.store.selectSnapshot(AssetsState.fetchedAssets);

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Crop-${key}`,
        component: 'Cropper',
      }),
    );
    this.store.dispatch(new LogEvent('Cropper Opened'));

    // determine the source image
    let originalFile: File | null = null;
    let sourceImageUrl: string | null = null;
    let savedState: CropperState = null;

    if (rawImages) {
      // handle logo/iconTV assets
      if (key === 'logo' || key === 'iconTv') {
        originalFile =
          rawImages[key] || (rawImages['rawLogo'] ? rawImages['rawLogo'] : null);
      } else {
        // handle splashscreen assets
        originalFile =
          rawImages[key] ||
          (rawImages['rawSplashscreen'] ? rawImages['rawSplashscreen'] : null);
      }
    }
    // set the source image URL
    if (originalFile) {
      sourceImageUrl = URL.createObjectURL(originalFile);
      savedState = this.store.selectSnapshot(AssetsState.cropperStates)?.[key];
    } else if (assets[key]) {
      sourceImageUrl = constructAssetUrl(assets[key]);
    } else {
      console.error(`No source image found for key: ${key}`);

      return;
    }
    const width = ASSETS_DIMENSIONS.find(dimension => dimension.name === key)?.width;
    const height = ASSETS_DIMENSIONS.find(dimension => dimension.name === key)?.height;
    const dialogRef = this.matDialog.open(ImageCropperComponent, {
      data: {
        sourceImageUrl,
        aspectRatio: width / height,
        width,
        height,
        key,
        savedState,
      },
      disableClose: true,
      panelClass: 'cropper-dialog',
      maxWidth: '95vw',
    });

    const result = await dialogRef.afterClosed().toPromise();

    if (result) {
      const file = new File([result], key, { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(file);
      this.storeAsset(key, file, imageUrl);
      this.postImageAssets();
    }
  }

  async replaceAsset(key: string) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg';

    // event tracking
    this.store.dispatch(
      new SetEventProperties({
        name: `Replace-${key}`,
        component: 'ImageUpload',
      }),
    );
    this.store.dispatch(new LogEvent('Image Replaced'));

    input.onchange = async (event: any) => {
      const file = event.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      if (!file) {
        return;
      }
      this.setRawImage(key, file);
      this.storeAsset(key, file, imageUrl);
      this.saveCropperState(key, null);
      this.postImageAssets();
    };

    input.click();
  }

  onSaveSettings() {
    const dialogRef = this.matDialog.open(SettingsConfirmationModalComponent, {
      width: '720px',
      maxWidth: '90vw',
    });
    dialogRef.afterClosed().subscribe((result: SettingsConfirmationResult) => {
      if (result?.save) {
        this.saveSettings();
      }
      if (result?.redirect) {
        this.isLoading$
          .pipe(
            filter(isLoading => isLoading === false),
            take(1),
          )
          .subscribe(() => {
            setTimeout(() => {
              const hasError = !!this.store.selectSnapshot(
                AppConfigurationState.apiErrorMessage,
              );
              if (!hasError) {
                const appId = this.route.snapshot.params['appId'];
                this.router.navigate(['/publishing/', appId]);
              }
            }, 300);
          });
      }
    });
  }

  @Dispatch()
  saveSettings() {
    return new UpdateAssetsRequest();
  }

  @Dispatch()
  storeAsset(key: string, file: File, url: string) {
    return new StoreAsset(key, file, url);
  }

  @Dispatch()
  postImageAssets() {
    return new PostImageAssetsRequest();
  }

  @Dispatch()
  setRawImage(key: string, image: File) {
    return new SetRawImage(key, image);
  }

  @Dispatch()
  saveCropperState(key: string, data: CropperState) {
    return new SaveCropperState(key, data);
  }

  @Dispatch()
  fetchAssets(appId: number) {
    return new FetchAssetsRequest(appId);
  }

  @Dispatch()
  emtyAssets() {
    return new EmptyAssetState();
  }

  @Dispatch()
  setCurrentAppId(appId: number) {
    return new SetCurrentApp(appId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
